
import React from 'react';

interface LoadingProps {
  size?: 'small' | 'medium' | 'large';
  message?: string;
  fullScreen?: boolean;
  delay?: number; // Add delay option to prevent flickering for fast loads
}

const OptimizedLoading: React.FC<LoadingProps> = ({ 
  size = 'medium', 
  message = 'Loading...', 
  fullScreen = false,
  delay = 0
}) => {
  const [show, setShow] = React.useState(delay === 0);
  
  // Only show the loading indicator after the specified delay
  // This prevents flickering for fast operations
  React.useEffect(() => {
    if (delay === 0) return;
    
    const timer = setTimeout(() => {
      setShow(true);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [delay]);
  
  if (!show) return null;
  
  // Determine size based on prop - simplified for performance
  const sizeClasses = {
    small: 'h-4 w-4 border',
    medium: 'h-8 w-8 border-2',
    large: 'h-12 w-12 border-3'
  };

  const containerClass = fullScreen 
    ? 'fixed inset-0 flex items-center justify-center bg-background/50 z-50' 
    : 'flex items-center justify-center min-h-[50px]';

  return (
    <div className={containerClass} role="status" aria-live="polite">
      <div className="flex flex-col items-center">
        <div 
          className={`${sizeClasses[size]} rounded-full border-t-primary border-r-transparent border-b-primary border-l-transparent animate-spin`}
        />
        {message && (
          <p className="mt-1 text-xs text-muted-foreground">{message}</p>
        )}
      </div>
    </div>
  );
};

export default OptimizedLoading;
